import { httpGet, httpPut } from '@/api/http';
import { V2_USER_SETTINGS } from '@/constants/endpoint';
import { useMutation, useQueryClient, type UseQueryReturnType } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { useAuthenticatedQuery } from '@/utils/query';

export interface UserSettingsDTO {
  settings: UserSettings;
}

export interface UserSettings {
  focusOnShortcut: boolean;
  showPickupBanner: boolean;
  playPickupSound: boolean;
  callOnShortcut: boolean;
  phoneEnabled: boolean;
  lastClientVersion: string | null;
  hotkeyEnabled: boolean;
  locale: string;
}

export const USER_SETTINGS_QUERY_KEY = 'userSettings';

const userSettingsQuery = {
  queryKey: [USER_SETTINGS_QUERY_KEY],
  queryFn: async () => {
    try {
      return await httpGet<UserSettingsDTO>(V2_USER_SETTINGS);
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },
  retry: (failureCount: number, error: any) => {
    if (error.response?.status === 404) return false;
    return failureCount < 3;
  },
};

export const useUserSettings = (): UseQueryReturnType<UserSettingsDTO | null, AxiosError> => {
  return useAuthenticatedQuery(userSettingsQuery);
};

export const useUpdateUserSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: UserSettings) => httpPut<{ settings: UserSettings }, UserSettingsDTO>(V2_USER_SETTINGS, { settings }),

    onSuccess: (data) => {
      queryClient.setQueryData([USER_SETTINGS_QUERY_KEY], data);
    },
  });
};
